import { normalize, parse, same, type Viewport } from '$lib/viewport';
import { triggerPreferencesSync } from './persistenceCoordinator';

export type Tab = 'map' | 'dashboard' | 'fleet';
const BASEMAP_KEYS = [
  'google-roadmap',
  'google',
  'esri',
  'esri-streets',
  'streets',
] as const;
export type Basemap = (typeof BASEMAP_KEYS)[number];

export function isBasemap(v: unknown): v is Basemap {
  return typeof v === 'string' && (BASEMAP_KEYS as readonly string[]).includes(v);
}
export type Theme = 'light' | 'dark';

function initialTheme(): Theme {
  if (typeof document !== 'undefined') {
    const t = document.documentElement.dataset.theme;
    if (t === 'light' || t === 'dark') return t;
  }
  return 'light';
}

function initialTab(): Tab {
  if (typeof window !== 'undefined') {
    try {
      const saved = localStorage.getItem('ph-tab');
      if (saved === 'map' || saved === 'dashboard' || saved === 'fleet') return saved;
    } catch {
      /* ignore */
    }
  }
  return 'map';
}

function initialMapViewport(): Viewport | null {
  if (typeof window !== 'undefined') {
    try {
      const raw = localStorage.getItem('ph-map-viewport');
      if (raw) {
        return parse(JSON.parse(raw));
      }
    } catch {
      /* ignore */
    }
  }
  return null;
}

function initialOrientationSeen(): boolean {
  if (typeof window !== 'undefined') {
    try {
      return localStorage.getItem('ph-orientation-seen') === '1';
    } catch {
      /* ignore */
    }
  }
  return false;
}

function initialBasemap(): Basemap {
  if (typeof window !== 'undefined') {
    try {
      const saved = localStorage.getItem('ph-basemap');
      if (isBasemap(saved)) {
        return saved;
      }
    } catch {
      /* ignore */
    }
  }
  return 'google-roadmap';
}

let tab = $state<Tab>(initialTab());
let theme = $state<Theme>(initialTheme());
let basemap = $state<Basemap>(initialBasemap());
let mapViewport = $state<{ center: [number, number]; zoom: number } | null>(initialMapViewport());
let selectedId = $state<string | null>(null);
// Bumped on every selectedId assignment — even re-selecting the same site.
// MarkersLayer flies on a seq change so re-picking an already-selected site
// re-flies (source selectSite always flew), while filter/auth-driven marker
// rebuilds (which don't touch selectedId) don't trigger a spurious fly.
let selSeq = $state(0);
// Origin of the current selection ('map' | 'list' | 'dashboard' | 'create'),
// set via select(). Lets site_selected carry a `source` for analytics.
let selectSource = $state<string>('map');
let showRoofs = $state(true);
let showCabinets = $state(true);
let heat = $state(false);
let driverHeat = $state(false);
let legendOpen = $state(true);
let authOpen = $state(false);
let adminOpen = $state(false);
let teamOpen = $state(false);
let detailId = $state<string | null>(null);
let helpOpen = $state(false);
// Persisted "has this user ever seen the Fleet orientation card" flag —
// round-tripped through preferences.svelte.ts like legendOpen, plus a
// ph-* localStorage key so signed-out users also only get the auto-show once.
let orientationSeen = $state(initialOrientationSeen());
// Transient open flag for the card itself, mirroring helpOpen: never synced
// to preferences on its own, only orientationSeen is.
let orientationOpen = $state(false);
let layersSheetOpen = $state(false);
let sheetState = $state<'peek' | 'half' | 'full'>('peek');
// One-shot fly request: components outside the map subtree (e.g. the add/edit
// form) set this; MapInteractions watches it and drives map.flyTo. seq makes
// repeated flies to the same point still fire.
let flyReq = $state<{
  lat: number;
  lng: number;
  zoom: number;
  duration: number;
  seq: number;
} | null>(null);
let flySeq = 0;

export const ui = {
  get tab() {
    return tab;
  },
  set tab(v: Tab) {
    tab = v;
    try {
      localStorage.setItem('ph-tab', v);
    } catch {
      /* ignore */
    }
    triggerPreferencesSync();
  },
  get mapViewport(): Viewport | null {
    return mapViewport;
  },
  setViewport(center: [number, number], zoom: number) {
    const next = normalize(center, zoom);
    if (same(mapViewport, next)) return;
    mapViewport = next;
    try {
      localStorage.setItem('ph-map-viewport', JSON.stringify(next));
    } catch {
      /* ignore */
    }
    triggerPreferencesSync();
  },
  get theme() {
    return theme;
  },
  get basemap() {
    return basemap;
  },
  set basemap(v: Basemap) {
    basemap = v;
    try {
      localStorage.setItem('ph-basemap', v);
    } catch {
      /* ignore */
    }
    triggerPreferencesSync();
  },
  get selectedId() {
    return selectedId;
  },
  set selectedId(v: string | null) {
    selectedId = v;
    selSeq += 1;
  },
  get selSeq() {
    return selSeq;
  },
  get selectSource() {
    return selectSource;
  },
  /** Select a site and record where the selection came from (analytics). */
  select(id: string, source: string) {
    selectSource = source;
    this.selectedId = id; // setter bumps selSeq so MarkersLayer re-flies
  },
  get showRoofs() {
    return showRoofs;
  },
  set showRoofs(v: boolean) {
    showRoofs = v;
    triggerPreferencesSync();
  },
  get showCabinets() {
    return showCabinets;
  },
  set showCabinets(v: boolean) {
    showCabinets = v;
    triggerPreferencesSync();
  },
  set heat(v: boolean) {
    heat = v;
    if (v) driverHeat = false;
    triggerPreferencesSync();
  },
  set driverHeat(v: boolean) {
    driverHeat = v;
    if (v) heat = false;
    triggerPreferencesSync();
  },
  get heat() {
    return heat;
  },
  get driverHeat() {
    return driverHeat;
  },
  get legendOpen() {
    return legendOpen;
  },
  set legendOpen(v: boolean) {
    legendOpen = v;
    triggerPreferencesSync();
  },
  get authOpen() {
    return authOpen;
  },
  set authOpen(v: boolean) {
    authOpen = v;
  },
  get adminOpen() {
    return adminOpen;
  },
  set adminOpen(v: boolean) {
    adminOpen = v;
  },
  /** Standalone Team/Access panel (commercial entry point; admins use the Admin modal's Access tab). */
  get teamOpen() {
    return teamOpen;
  },
  set teamOpen(v: boolean) {
    teamOpen = v;
  },
  get detailId() {
    return detailId;
  },
  set detailId(v: string | null) {
    detailId = v;
  },
  get helpOpen() {
    return helpOpen;
  },
  set helpOpen(v: boolean) {
    helpOpen = v;
  },
  get orientationSeen() {
    return orientationSeen;
  },
  set orientationSeen(v: boolean) {
    orientationSeen = v;
    try {
      localStorage.setItem('ph-orientation-seen', v ? '1' : '0');
    } catch {
      /* ignore */
    }
    triggerPreferencesSync();
  },
  get orientationOpen() {
    return orientationOpen;
  },
  set orientationOpen(v: boolean) {
    orientationOpen = v;
  },
  get layersSheetOpen() {
    return layersSheetOpen;
  },
  set layersSheetOpen(v: boolean) {
    layersSheetOpen = v;
  },
  get sheetState() {
    return sheetState;
  },
  set sheetState(v: 'peek' | 'half' | 'full') {
    sheetState = v;
  },
  cycleSheet() {
    sheetState = sheetState === 'peek' ? 'half' : sheetState === 'half' ? 'full' : 'peek';
  },

  get flyReq() {
    return flyReq;
  },
  requestFly(lat: number, lng: number, zoom: number, duration = 0.8) {
    flySeq += 1;
    flyReq = { lat, lng, zoom, duration, seq: flySeq };
  },

  setTheme(v: Theme) {
    theme = v;
    if (typeof document !== 'undefined') document.documentElement.dataset.theme = v;
    try {
      localStorage.setItem('ph-theme', v);
    } catch {
      /* ignore */
    }
    triggerPreferencesSync();
  },
  toggleTheme() {
    this.setTheme(theme === 'dark' ? 'light' : 'dark');
  },

  // Solar density and driver-dwell heatmaps are mutually exclusive (one set of
  // heat tiles at a time), mirroring the source toggle behaviour.
  toggleHeat() {
    heat = !heat;
    if (heat) driverHeat = false;
    triggerPreferencesSync();
  },
  toggleDriverHeat() {
    driverHeat = !driverHeat;
    if (driverHeat) heat = false;
    triggerPreferencesSync();
  },
};
